import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, Polygon, Polyline, useJsApiLoader } from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "360px",
  borderRadius: "8px",
};

const DEFAULT_CENTER = { lat: 30.3753, lng: 69.3451 }; // Pakistan centroid

const libraries = ["places"];

const STROKE = "#0d6efd";
const FILL = "#0d6efd";

const normalizePoints = (points) => {
  if (!Array.isArray(points)) return [];
  return points
    .map((p) => ({
      lat: Number(p.lat ?? p.Lat),
      lng: Number(p.lng ?? p.Lng),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
};

const pointsSignature = (pts) =>
  pts.map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`).join("|");

/** Numbered circle marker so vertices stay visible while drawing. */
const buildVertexIcon = (index, isLast) => {
  if (!window.google?.maps) return undefined;
  const fill = isLast ? "#dc3545" : STROKE;
  const label = String(index + 1);
  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="12" fill="${fill}" stroke="#ffffff" stroke-width="3"/>
      <text x="14" y="18" text-anchor="middle" font-size="11" font-family="Arial,sans-serif" font-weight="700" fill="#ffffff">${label}</text>
    </svg>`
  );
  return {
    url: `data:image/svg+xml;charset=UTF-8,${svg}`,
    scaledSize: new window.google.maps.Size(28, 28),
    anchor: new window.google.maps.Point(14, 14),
  };
};

/**
 * Click map to add polygon vertices. Shows points + edge lines while drawing.
 * value: [{ lat, lng }, ...]
 */
const ZonePolygonMap = ({
  value = [],
  onChange,
  serviceCity = "",
  countryCode = "PAK",
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: "marketplace-zone-map",
    googleMapsApiKey: apiKey,
    libraries,
  });

  const mapRef = useRef(null);
  const fittedSignatureRef = useRef("");
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const points = useMemo(() => normalizePoints(value), [value]);

  const fitToPoints = useCallback((map, pts) => {
    if (!map || !pts?.length || !window.google?.maps) return;
    if (pts.length === 1) {
      map.panTo(pts[0]);
      map.setZoom(Math.max(map.getZoom() || 12, 14));
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 56);
  }, []);

  // Geocode city once for empty drawings only (do not fight user while placing points)
  useEffect(() => {
    if (!isLoaded || !window.google?.maps || !serviceCity) return;
    if (points.length > 0) return;

    const geocoder = new window.google.maps.Geocoder();
    const address = [serviceCity, countryCode === "PAK" ? "Pakistan" : countryCode]
      .filter(Boolean)
      .join(", ");

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        const next = { lat: loc.lat(), lng: loc.lng() };
        setCenter(next);
        if (mapRef.current) {
          mapRef.current.setCenter(next);
          mapRef.current.setZoom(13);
        }
      }
    });
  }, [isLoaded, serviceCity, countryCode, points.length]);

  // Fit bounds only when loading an existing polygon (or first paint of a saved shape),
  // not after every click while drawing.
  useEffect(() => {
    if (!mapRef.current || points.length === 0) {
      if (points.length === 0) fittedSignatureRef.current = "";
      return;
    }
    const sig = pointsSignature(points);
    // Fit when shape is first loaded (≥3) or when opening edit with existing points
    if (points.length >= 3 && fittedSignatureRef.current !== sig && fittedSignatureRef.current === "") {
      fitToPoints(mapRef.current, points);
      fittedSignatureRef.current = sig;
    }
  }, [points, fitToPoints]);

  const handleMapClick = (e) => {
    if (!e?.latLng) return;
    const next = [
      ...points,
      { lat: e.latLng.lat(), lng: e.latLng.lng() },
    ];
    // Mark that user is actively drawing so we don't re-fit on edits
    if (fittedSignatureRef.current === "") {
      fittedSignatureRef.current = "drawing";
    }
    onChange?.(next);
  };

  const handleClear = () => {
    fittedSignatureRef.current = "";
    onChange?.([]);
  };

  const handleUndo = () => {
    if (points.length === 0) return;
    const next = points.slice(0, -1);
    if (next.length === 0) fittedSignatureRef.current = "";
    onChange?.(next);
  };

  if (!apiKey) {
    return (
      <div className="alert alert-warning mb-0">
        Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in SuperAdmin <code>.env</code> to draw zone polygons.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-danger mb-0">
        Failed to load Google Maps. Check the API key and that Maps JavaScript API is enabled.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className="d-flex align-items-center justify-content-center border rounded text-muted"
        style={mapContainerStyle}
      >
        Loading map…
      </div>
    );
  }

  const showPolyline = points.length >= 2 && points.length < 3;
  const showPolygon = points.length >= 3;

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleUndo} disabled={points.length === 0}>
          Undo last point
        </button>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleClear} disabled={points.length === 0}>
          Clear polygon
        </button>
        <small className="text-muted">
          Click the map to add vertices ({points.length} point{points.length === 1 ? "" : "s"}
          {points.length > 0 && points.length < 3 ? " — need at least 3" : ""}
          {points.length >= 3 ? " — closed area" : ""}).
        </small>
      </div>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={6}
        onLoad={(map) => {
          mapRef.current = map;
          if (points.length > 0) {
            fitToPoints(map, points);
            fittedSignatureRef.current = points.length >= 3 ? pointsSignature(points) : "drawing";
          } else if (center) {
            map.setCenter(center);
            map.setZoom(serviceCity ? 13 : 6);
          }
        }}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {/* Open edge lines while placing the first edges (2 points) */}
        {showPolyline && (
          <Polyline
            path={points}
            options={{
              strokeColor: STROKE,
              strokeOpacity: 1,
              strokeWeight: 3,
              clickable: false,
              zIndex: 1,
            }}
          />
        )}

        {/* Closed filled polygon once we have 3+ points */}
        {showPolygon && (
          <Polygon
            paths={points}
            options={{
              fillColor: FILL,
              fillOpacity: 0.22,
              strokeColor: STROKE,
              strokeOpacity: 0.95,
              strokeWeight: 3,
              clickable: false,
              zIndex: 2,
            }}
          />
        )}

        {/* Numbered vertex markers — visible from the first click */}
        {points.map((p, idx) => (
          <Marker
            key={`vertex-${idx}-${p.lat}-${p.lng}`}
            position={p}
            clickable={false}
            zIndex={10 + idx}
            icon={buildVertexIcon(idx, idx === points.length - 1)}
            title={`Point ${idx + 1}`}
          />
        ))}
      </GoogleMap>
      {points.length > 0 && points.length < 3 && (
        <small className="text-muted d-block mt-1">
          Points appear as numbered circles; a line connects them as you keep clicking.
        </small>
      )}
    </div>
  );
};

export default ZonePolygonMap;
export { normalizePoints };
